import { JSX, useMemo } from "react";

import { Box, Paper, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useHistoryChartQuery } from "src/api/locationChartQueries.api";
import DifferenceChip from "src/components/Difference/DifferenceChip";
import IntervalType from "src/constants/intervalType.enum";
import MeasureType from "src/constants/measureType.enum";
import sharedStyles from "src/constants/styles";
import { formatNum, getCurrentTotalCount, getDifference, getPastTotalCount } from "src/lib/number.helpers";

import ExpectedVisitorsComponent from "./components/ExpectedVisitorsComponent";
import { type DashboardComponentParams } from "../interfaces/DashboardComponentParams";
import getStyles from "./helper/infoBar.helper";

function VisitorsTodayComponent({ selectedLocation }: DashboardComponentParams): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();

  const { data: measuresData } = useHistoryChartQuery(selectedLocation?.Id, IntervalType.Day, null, MeasureType.Count);

  const prevIsoYearDate = dayjs(measuresData?.pastDateRange.start);
  const chartData = useMemo(() => measuresData?.data ?? [], [measuresData?.data]);
  const diff = useMemo(() => getDifference(measuresData), [measuresData]);
  const currentPeriodSum = useMemo(() => getCurrentTotalCount(chartData), [chartData]);
  const pastPeriodSum = useMemo(() => getPastTotalCount(chartData), [chartData]);

  return (
    <Paper sx={getStyles(theme).fullheight}>
      <Box sx={getStyles(theme).innerBox}>
        <Box sx={getStyles(theme).titleBox}>
          <Typography
            color={theme.palette.text.secondary}
            fontSize={theme.fontSizes.sm}
            fontWeight={theme.typography.fontWeightRegular}
            textTransform='uppercase'
          >
            {t("Visitors today")}
          </Typography>
        </Box>
        <Box mt={3}>
          <Box sx={sharedStyles(theme).centerFlexbox}>
            <Typography variant='h2'>{formatNum(currentPeriodSum)}</Typography>
            <DifferenceChip difference={diff} fontSize={theme.fontSizes.md} />
          </Box>
          <Box sx={sharedStyles(theme).centerFlexbox} mt={1}>
            <Typography variant='body2' color={theme.palette.text.secondary}>
              {formatNum(pastPeriodSum)} {t("previous year")}
            </Typography>
          </Box>
        </Box>
        <ExpectedVisitorsComponent
          date={prevIsoYearDate}
          intervalType={IntervalType.Day}
          locationId={selectedLocation.Id}
          chartData={chartData}
        />
      </Box>
    </Paper>
  );
}

export default VisitorsTodayComponent;
